/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { findIndex, fromPairs, partial } from 'lodash';
import { MutateStateEvent, MutateStateReducer } from '../observable/state';

/**
 * Allows to describe reducer for object-like state from nested reducers.
 * <code>
 *   combineReducers({
 *     counter: counterReducer,
 *     user: userReducer,
 *   })
 * </code>
 *
 * Reducer returns new merged state if and only if one of reducers returns new nested state.
 * @param reducers
 */
export function combineReducers(reducers: Hash<MutateStateReducer<any>>): MutateStateReducer<any> {
  const reducerKeys = Object.keys(reducers);

  return (state, event) => {
    const reducedValues = reducerKeys
      .map((key) => reducers[key](state[key], event));
    const isTransformed = reducedValues.some((reduced, i) => reduced !== state[reducerKeys[i]]);

    return isTransformed ? {
        ...state,
        ...fromPairs(reducerKeys.map((key, i) => [key, reducedValues[i]])),
      } :
      state;
  };
}

/**
 * Builds reducer which calls all given reducers in order. Each subsequent reducer gets state from the previous one.
 * @param reducers
 */
export function composeReducers(...reducers: MutateStateReducer<any>[]): MutateStateReducer<any> {
  return (state, event) => reducers.reduce((nextState, reducer) => reducer(nextState, event), state);
}

/**
 * Creates reducer for array-like data which updates array if one of array element has been updated.
 * Array element to be updated determined by the given <code>predicate</code>.
 * The element is updated using given reducer.
 *
 * @param reducer
 * @param predicate accepts two parameters: current event and element of array
 */
export function updateInArrayReducer<T>(reducer: MutateStateReducer<T>,
                                        predicate: PredicateFn2<MutateStateEvent, T>): MutateStateReducer<T[]> {
  return (entities, event) => {
    const foundIndex = findIndex(entities, partial(predicate, event));

    if (foundIndex >= 0) {
      const foundEntity = entities[foundIndex];
      const reducedEntity = reducer(foundEntity, event);
      const isTransformed = reducedEntity !== foundEntity;

      return isTransformed
        ? entities.slice(0, foundIndex).concat([reducedEntity]).concat(entities.slice(foundIndex + 1)) : entities;
    } else {
      return entities;
    }
  };
}

/**
 * Creates reducer for array-like data which updates array if one of array element should be removed.
 * Array element to be removed determined by the given <code>predicate</code>.
 *
 * @param predicate accepts two parameters: current event and element of array
 */
export function removeFromArrayReducer<T>(predicate: PredicateFn2<MutateStateEvent, T>): MutateStateReducer<T[]> {
  return (entities, event) => {
    const foundIndex = findIndex(entities, partial(predicate, event));

    if (foundIndex >= 0) {
      return entities.slice(0, foundIndex).concat(entities.slice(foundIndex + 1));
    } else {
      return entities;
    }
  };
}

export type OnChangeMutateStateReducer<T> = (oldState: T, newState: T, event: MutateStateEvent) => T;

/**
 * Creates compound reducer which applies the second reducer only when first changes a state.
 */
export function onChangeReducer<T>(primaryReducer: MutateStateReducer<T>,
                                   ...onChangeReducers: OnChangeMutateStateReducer<T>[]): MutateStateReducer<T> {
  return (state, event) => {
    const newState = primaryReducer(state, event);
    return state === newState ? state : onChangeReducers.reduce(
      (nextState, reducer) => reducer(state, nextState, event),
      newState);
  };
}

export function toOnChangeReducer<T>(reducer: MutateStateReducer<T>): OnChangeMutateStateReducer<T> {
  return (_, state, event) => reducer(state, event);
}
